import {getCookie} from "./Cookies";

/**
 * Fixed Filters Plugin
 *
 * Attach the filter to the top of the list if the user is scrolling inside the filter list
 */
export class FixedFilters {
    private static instanceMap: Map<HTMLElement, FixedFilters> = new Map();

    protected baseElement: HTMLSelectElement;
    protected scrollTo: string = '';
    protected flyoutToggle: HTMLElement;
    protected flyoutBody: HTMLElement;
    protected userChoice: boolean;
    protected userHasChoosen: boolean;
    protected isFixed: boolean;
    protected waypointTopElement: HTMLElement;
    protected waypointBottomElement: HTMLElement;
    protected filterListElement: HTMLElement;
    protected waypointTopInViewport = false;
    protected filterListInViewport = false;
    protected intersectionObserver: IntersectionObserver;
    protected resizeObserver: ResizeObserver;

    constructor(baseElement: HTMLSelectElement) {
        this.baseElement = baseElement;
        this.flyoutToggle = baseElement.querySelector('.filter-boxes__toggle');
        this.flyoutBody = baseElement.querySelector('.filter-boxes__body');
        this.waypointTopElement = this.baseElement.parentElement.parentElement.querySelector('.filter-boxes-waypoint');
        this.waypointBottomElement = this.baseElement.parentElement.parentElement.querySelector('.filter-boxes-waypoint-bottom');
        this.filterListElement = this.baseElement.parentElement.parentElement.querySelector('.product-list, .table-wrapper');

        // show the filter if the required cookie is set
        if (getCookie('filter-boxes-toggle') === 'open') {
            this.show();
            this.userChoice = true;
            this.userHasChoosen = true;
        }

        // hide the filter on md> viewports by default
        if (window.Viewport.mediaBreakpointDown('md')) {
            this.hide();
        }

        // initialize event listeners
        window.addEventListener('resize', this.resizeHandler, {passive: true});
        window.addEventListener('scroll', this.scrollHandler, {passive: true});

        this.flyoutToggle.addEventListener('click', this.toggleHandler, {passive: false});

        this.baseElement.addEventListener('plandent.fixedFilter.updateHiddenFields', this.disableEmptyLists, {passive: true});

        document.addEventListener('plandent.headernavigation.show', this.handleHeaderNavigationFixedShow);
        document.addEventListener('plandent.headernavigation.hide', this.handleHeaderNavigationFixedHide);

        // observer to attach the filter to the top of the page
        this.intersectionObserver = new IntersectionObserver(this.intersectionObserverCallback);
        if (this.filterListElement) this.intersectionObserver.observe(this.filterListElement);
        if (this.waypointTopElement) this.intersectionObserver.observe(this.waypointTopElement);

        this.resizeObserver = new ResizeObserver(this.resizeObserverCallback);
        if(this.baseElement.parentElement) this.resizeObserver.observe(this.baseElement.parentElement);
    }

    /**
     * Handler for resize events
     */
    protected resizeHandler = () => {

        if (window.Viewport.mediaBreakpointDown('lg')) {
            this.detachFilter();
        }

        // refresh the observer to reattach or detach the filter if the user is already in the list
        this.observerRefresh();
    }

    /**
     * Handler for scroll events
     */
    protected scrollHandler = () => {
        if (this.isFixed && !this.flyoutToggle.classList.contains('filter-boxes__toggle--hidden')) {
            this.hide();
        }
    }

    /**
     * Handler for click events
     *
     * @param e
     */
    protected toggleHandler = (e: Event) => {
        this.toggle();

        // Set cookie for toggle state
        const d = new Date();
        d.setTime(d.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30 days
        const expires = "expires=" + d.toUTCString();
        const state = this.userChoice ? 'open' : 'collapsed';

        document.cookie = 'filter-boxes-toggle=' + state + ';' + expires + ';path=/';

        e.stopPropagation();
    }

    /**
     * Adjust the width of the base elements
     * @param width Width to be set if not calculated automatically
     */
    protected adjustWidth = (width?: number) => {
        const parentWidth = width || this.baseElement.parentElement.offsetWidth;
        this.baseElement.setAttribute('style',`--width: ${parentWidth}px;`);
    }

    /**
     * Toggle the filter flyout
     */
    public toggle = () => {
        if (this.flyoutBody.style.display === 'none') {
            this.show();

            this.userHasChoosen = true;
            this.userChoice = true;
        }
        else {
            this.hide();

            this.userHasChoosen = false;
            this.userChoice = false;
        }
    }

    /**
     * Show the filter flyout
     */
    public show = () => {
        this.baseElement.parentElement.classList.remove('product-filters__hidden');
        this.flyoutToggle.classList.remove('filter-boxes__toggle--hidden');
        this.flyoutBody.style.display = null;
    }

    /**
     * Hide the filter flyout
     */
    public hide = () => {
        this.baseElement.parentElement.classList.add('product-filters__hidden');
        this.flyoutToggle.classList.add('filter-boxes__toggle--hidden');
        this.flyoutBody.style.display = 'none';

        // hide all dropdowns
        const dropdowns: NodeListOf<HTMLButtonElement> = this.baseElement.querySelectorAll('.dropdown.bootstrap-select .dropdown-toggle');
        dropdowns.forEach((dropdownToggle) => {
            if (dropdownToggle.classList.contains('show')) {
                const wrapper = dropdownToggle.closest('.dropdown.bootstrap-select');
                const select = wrapper.querySelector('select');

                $(select).selectpicker('toggle');
            }
        });
    }

    /**
     * Disable lists that have no entries
     */
    public disableEmptyLists = () => {
        const listElements: NodeListOf<HTMLUListElement> = this.baseElement.querySelectorAll('.dropdown.bootstrap-select');

        listElements.forEach((selectList) => {
            const wrapper = selectList.closest('.dropdown.bootstrap-select');
            const wrapperFilterCol = selectList.closest('.filter-col');
            const button = wrapper.querySelector('button');

            if (selectList.querySelectorAll('select option').length === 0) {
                wrapperFilterCol.classList.add('d-none', 'filter-has-no-values');
                wrapper.classList.add('disabled');
                button.disabled = true;
            } else {
                wrapperFilterCol.classList.remove('d-none', 'filter-has-no-values');
                wrapper.classList.remove('disabled');
                button.disabled = false;
            }
        })
    }

    /**
     * Callback function for the observer to attach the filter to the top of the page
     *
     * @param entries
     * @param obj
     */
    protected intersectionObserverCallback = (entries: IntersectionObserverEntry[], obj: IntersectionObserver) => {
        // set variables if all required entries are visible
        entries.forEach(entry => {
            if (this.filterListElement && entry.target === this.filterListElement) {
                this.filterListInViewport = entry.isIntersecting;
            }
            if (this.waypointTopElement && entry.target === this.waypointTopElement) {
                this.waypointTopInViewport = entry.isIntersecting;
            }
        });

        if (!this.waypointTopInViewport && this.filterListInViewport) {
            this.attachFilter();
        } else {
            this.detachFilter();
        }
    }

    protected resizeObserverCallback = (entries: ResizeObserverEntry[], obj: ResizeObserver) => {
        entries.forEach(entry => {
            // contentBoxSize is actual size after padding calcs
           if(entry.borderBoxSize && entry.borderBoxSize[0]) {
               this.adjustWidth(entry.borderBoxSize[0].inlineSize);
           }
        });
    }

    /**
     * Refresh the observer (required for resize events)
     */
    protected observerRefresh = () => {
        if (this.filterListElement) {
            this.intersectionObserver.unobserve(this.filterListElement);
            this.intersectionObserver.observe(this.filterListElement);
        }
        if (this.waypointTopElement) {
            this.intersectionObserver.unobserve(this.waypointTopElement);
            this.intersectionObserver.observe(this.waypointTopElement);
        }
    }

    /**
     * Attach the filter to the top of the page
     */
    protected attachFilter = () => {
        this.isFixed = true;

        this.baseElement.parentElement.style.height = this.baseElement.parentElement.offsetHeight + 'px';
        this.baseElement.classList.add('filter-boxes--fixed');
    }

    /**
     * Detach the filter from the top of the page
     */
    protected detachFilter = () => {
        this.isFixed = false;

        this.baseElement.parentElement.style.height = '';
        this.baseElement.classList.remove('filter-boxes--fixed');
    }

    protected handleHeaderNavigationFixedShow = () => {
        if (this.isFixed) {
            this.hide();
        }
        this.baseElement.classList.add('filter-boxes--hidden');
    }

    protected handleHeaderNavigationFixedHide = () => {
        this.baseElement.classList.remove('filter-boxes--hidden');
    }

    /**
     * Create FixedFilters-Instances from a NodeList of HTMLSelectElements or a query selector string
     *
     * @param {NodeListOf<HTMLSelectElement>|string} elementList
     * @param disableEmptyLists disable all empty lists
     */
    public static createInstances = (elementList: NodeListOf<HTMLSelectElement> | string, disableEmptyLists: boolean = false) => {
        if (typeof elementList === 'string') {
            elementList = document.querySelectorAll(elementList);
        }

        if (elementList.length > 0) {
            Array.from(elementList).forEach((el) => {
                const fixedFilter = FixedFilters.getOrCreateInstance(el);

                if (disableEmptyLists) {
                    fixedFilter.disableEmptyLists();
                }
            });
        }
    }

    /**
     * Get or create a new FixedFilter
     *
     * @param fixedFilter
     */
    public static getOrCreateInstance = (fixedFilter: HTMLSelectElement): FixedFilters => {
        if (!FixedFilters.instanceMap.has(fixedFilter)) {
            FixedFilters.instanceMap.set(fixedFilter, new FixedFilters(fixedFilter));
        }

        return FixedFilters.instanceMap.get(fixedFilter);
    }
}

/**
 * Initialize fixed filters
 */
export function initFixedFilters(elementList: string|HTMLElement = '.fixable-filter-boxes', disableEmptyLists: boolean = true) {
    FixedFilters.createInstances(elementList, disableEmptyLists);
}
